import { WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/DatePicker";
import { ESSENTIAL_CHECKS } from "@/components/DocumentsTab";
import { DOC_CHECKLIST } from "@/components/DocsChecklistTab";

const DOC_LABELS = DOC_CHECKLIST.reduce((m, s) => { s.items.forEach((i) => { m[i.key] = i.text; }); return m; }, {});

export default function DeviationSignoff({ vd, onPatch }) {
  const ec = vd?.essential_checks || {};
  const cl = vd?.doc_checklist || {};
  const so = vd?.deviation_signoff || {};
  const devs = [
    ...ESSENTIAL_CHECKS.filter((c) => (ec[c.key]?.status) === "Discrepancy Found").map((c) => ({
      kind: "ec", key: c.key, label: `${c.label}: discrepancy found`, remark: ec[c.key]?.remarks, resolution: ec[c.key]?.resolution || "",
    })),
    ...Object.keys(cl).filter((k) => cl[k]?.status === "Not Available").map((k) => ({
      kind: "cl", key: k, label: `Document not available — ${DOC_LABELS[k] || k}`, remark: cl[k]?.remarks, resolution: cl[k]?.resolution || "",
    })),
  ];

  if (devs.length === 0) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-3 flex items-center gap-2" data-testid="deviation-signoff-clear">
        <CheckCircle size={16} className="text-green-700" />
        <span className="text-xs text-green-800">No deviations — all essential checks and documents are clear. The final report can be released.</span>
      </div>
    );
  }

  const setResolution = (d, val) => onPatch({ [d.kind === "ec" ? "essential_checks" : "doc_checklist"]: { [d.key]: { resolution: val } } });
  const allResolved = devs.every((d) => (d.resolution || "").trim());
  const signed = so.acknowledged && (so.name || "").trim();

  return (
    <div className="rounded-lg border border-red-200 bg-red-50/60 p-4 space-y-3" data-testid="deviation-signoff">
      <div className="flex items-center gap-2"><WarningCircle size={18} className="text-red-600" /><div className="font-display text-base font-semibold text-red-700">Deviation Sign-off required</div></div>
      <p className="text-xs text-red-800/80">This report has {devs.length} red flag(s). Add a resolution note to each, then sign off below — the final report download &amp; email to bank stay locked until this is complete. (The watermarked Draft is always available.)</p>
      <div className="space-y-2">
        {devs.map((d) => (
          <div key={`${d.kind}-${d.key}`} className="rounded-md border bg-white p-2.5 space-y-1.5" data-testid={`deviation-${d.key}`}>
            <div className="text-xs font-semibold text-red-700">{d.label}</div>
            {d.remark ? <div className="text-[11px] text-muted-foreground">Finding: {d.remark}</div> : null}
            <Textarea value={d.resolution} onChange={(e) => setResolution(d, e.target.value)} placeholder="Resolution / how this was addressed (required)" className="text-xs min-h-[48px]" data-testid={`deviation-resolution-${d.key}`} />
          </div>
        ))}
      </div>
      <div className="rounded-md border bg-white p-3 space-y-2">
        <div className="grid sm:grid-cols-2 gap-2">
          <div className="space-y-1"><Label className="text-[11px]">Valuer name (sign-off)</Label><Input value={so.name || ""} onChange={(e) => onPatch({ deviation_signoff: { ...so, name: e.target.value } })} className="h-8 text-xs" data-testid="deviation-signoff-name" /></div>
          <div className="space-y-1"><Label className="text-[11px]">Date</Label><DatePicker value={so.date || ""} onChange={(v) => onPatch({ deviation_signoff: { ...so, date: v } })} placeholder="Select date" clearable className="w-full" data-testid="deviation-signoff-date" /></div>
        </div>
        <label className="flex items-start gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={!!so.acknowledged} onChange={(e) => onPatch({ deviation_signoff: { ...so, acknowledged: e.target.checked } })} className="mt-0.5" data-testid="deviation-signoff-ack" />
          <span>I have reviewed all deviations above and their resolutions, and I accept responsibility for releasing this report.</span>
        </label>
      </div>
      <div className={`text-xs font-medium ${allResolved && signed ? "text-green-700" : "text-red-700"}`} data-testid="deviation-signoff-status">
        {allResolved && signed ? "✓ All deviations resolved and signed off — final report unlocked." : (!allResolved ? "Add a resolution note to every red-flagged item." : "Enter the valuer name and tick the acknowledgement to unlock release.")}
      </div>
    </div>
  );
}
